import React, { type RefObject } from 'react';
import { useIntl } from 'react-intl';
import { MdChevronLeft, MdChevronRight, MdClose, MdOpenInFull } from 'react-icons/md';
import { TbLayoutBottombarCollapse, TbLayoutBottombarExpand } from 'react-icons/tb';
import {
  COUNT_BADGE_SIZE_XS,
  COUNT_BADGE_TONE_ACCENT,
  COUNT_BADGE_TONE_NEUTRAL,
  CountBadge,
  IconButton,
  Tabs,
  TABS_VARIANT_UNDERLINE,
  TOOLTIP_PLACEMENT_BOTTOM,
  TOOLTIP_PLACEMENT_TOP,
} from '@aibuild/ui';
import {
  FOOTER_VIEW_APPS,
  FOOTER_VIEW_MARKETPLACE,
  type FooterView,
} from './AppsFooter.constants';
import type { FooterTab } from './footerTab';
import { Actions, Header, Separator, TabsSlot, TitleButton } from './AppsFooter.styled';

interface FooterHeaderProps {
  readonly appsEnabled: boolean;
  readonly view: FooterView;
  readonly tabsShown: boolean;
  readonly tabs: readonly FooterTab[];
  readonly activeTab: string;
  readonly open: boolean;
  readonly expanded: boolean;
  readonly bodyId: string;
  readonly expandRef: RefObject<HTMLButtonElement | null>;
  readonly closeRef: RefObject<HTMLButtonElement | null>;
  readonly onTitle: (view: FooterView) => void;
  readonly onTab: (tab: string) => void;
  readonly onToggleOpen: () => void;
  readonly onExpand: () => void;
  readonly onClose: () => void;
}

const FooterHeader: React.FC<FooterHeaderProps> = ({
  appsEnabled,
  view,
  tabsShown,
  tabs,
  activeTab,
  open,
  expanded,
  bodyId,
  expandRef,
  closeRef,
  onTitle,
  onTab,
  onToggleOpen,
  onExpand,
  onClose,
}) => {
  const intl = useIntl();
  const labels: Record<FooterView, string> = {
    [FOOTER_VIEW_APPS]: intl.formatMessage({ id: 'apps.gallery.title', defaultMessage: 'Apps' }),
    [FOOTER_VIEW_MARKETPLACE]: intl.formatMessage({ id: 'marketplace.title', defaultMessage: 'Marketplace' }),
  };
  const tooltipPlacement = expanded ? TOOLTIP_PLACEMENT_BOTTOM : TOOLTIP_PLACEMENT_TOP;

  const title = (target: FooterView) => {
    const active = target === view;
    const Chevron = tabsShown ? MdChevronLeft : MdChevronRight;
    return (
      <TitleButton
        type="button"
        $active={active}
        aria-pressed={active}
        onClick={() => onTitle(target)}
        data-testid={`apps-footer__title-${target}`}
      >
        {labels[target]}
        {active && <Chevron size={20} aria-hidden="true" />}
      </TitleButton>
    );
  };

  const tabStrip = (target: FooterView) =>
    target === view &&
    tabsShown && (
      <TabsSlot>
        <Tabs
          ariaLabel={labels[target]}
          variant={TABS_VARIANT_UNDERLINE}
          bordered={false}
          dataTestId="apps-footer__tabs"
        >
          {tabs.map((tab) => {
            const active = tab.id === activeTab;
            return (
              <Tabs.Tab
                key={tab.id}
                active={active}
                onClick={() => onTab(tab.id)}
                dataTestId={`apps-footer__tab-${tab.id}`}
              >
                {tab.label}
                <CountBadge
                  size={COUNT_BADGE_SIZE_XS}
                  tone={active ? COUNT_BADGE_TONE_ACCENT : COUNT_BADGE_TONE_NEUTRAL}
                >
                  {tab.count}
                </CountBadge>
              </Tabs.Tab>
            );
          })}
        </Tabs>
      </TabsSlot>
    );

  const expandLabel = intl.formatMessage({ id: 'appsFooter.expand', defaultMessage: 'Open full gallery' });
  const closeLabel = intl.formatMessage({ id: 'appsFooter.close', defaultMessage: 'Close gallery' });
  const toggleLabel = open
    ? intl.formatMessage({ id: 'appsFooter.hide', defaultMessage: 'Hide row' })
    : intl.formatMessage({ id: 'appsFooter.show', defaultMessage: 'Show row' });

  return (
    <Header>
      {appsEnabled && (
        <>
          {title(FOOTER_VIEW_APPS)}
          {tabStrip(FOOTER_VIEW_APPS)}
          <Separator aria-hidden="true" />
        </>
      )}
      {title(FOOTER_VIEW_MARKETPLACE)}
      {tabStrip(FOOTER_VIEW_MARKETPLACE)}
      <Actions>
        {expanded ? (
          <IconButton
            key="close"
            ref={closeRef}
            ariaLabel={closeLabel}
            icon={<MdClose aria-hidden="true" />}
            onClick={onClose}
            tooltip={closeLabel}
            tooltipPlacement={tooltipPlacement}
            dataTestId="apps-footer__close"
          />
        ) : (
          <>
            <IconButton
              key="expand"
              ref={expandRef}
              ariaLabel={expandLabel}
              icon={<MdOpenInFull aria-hidden="true" />}
              onClick={onExpand}
              tooltip={expandLabel}
              tooltipPlacement={tooltipPlacement}
              dataTestId="apps-footer__expand"
            />
            <IconButton
              ariaLabel={toggleLabel}
              icon={open ? <TbLayoutBottombarCollapse aria-hidden="true" /> : <TbLayoutBottombarExpand aria-hidden="true" />}
              onClick={onToggleOpen}
              tooltip={toggleLabel}
              tooltipPlacement={tooltipPlacement}
              aria-expanded={open}
              aria-controls={bodyId}
              dataTestId="apps-footer__toggle"
            />
          </>
        )}
      </Actions>
    </Header>
  );
};

export default FooterHeader;
